import type { QuoterCalendarRow, QuoterPlanResult } from "../types";

/**
 * Almikia Epic EQ2 (TASK-098): the pure TypeScript mirror of the SQL engine's
 * plan and calendar part (`quoter_add_months`, `quoter_validate_custom_plan`,
 * `quoter_build_plan`). The SQL (numeric) is authoritative; every rule here
 * is the same one, so the demo (FakeRest) and the UI preview give the same
 * numbers. All dates are ISO strings `YYYY-MM-DD` (no time zone drift).
 */

const MIN_TERM_MONTHS = 1;
export const MAX_TERM_MONTHS = 36;

/**
 * Rounds half away from zero like PostgreSQL `round(numeric)`. The value is
 * first trimmed to 12 significant digits so a binary float error such as
 * 287049.99999999994 never changes the integer it stands for.
 */
export const roundHalfUp = (value: number): number => {
  const trimmed = Number(value.toPrecision(12));
  return trimmed < 0 ? -Math.round(-trimmed) : Math.round(trimmed);
};

/** Strips float noise from an amount (the SQL keeps exact numerics, never cents-rounded). */
export const cleanAmount = (value: number): number => Number(value.toFixed(6));

const pad = (value: number) => String(value).padStart(2, "0");

const daysInMonth = (year: number, monthIndex: number) =>
  new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();

/** The ISO date `YYYY-MM-DD` of a Date (UTC parts). */
export const toIsoDate = (date: Date): string =>
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;

/**
 * The business "today" (America/Mexico_City, as `quoter_today()`) of an
 * instant, as an ISO date.
 */
export const businessToday = (now: Date): string =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);

/** Adds whole days to an ISO date. */
export const addDays = (isoDate: string, days: number): string => {
  const [year, month, day] = isoDate.split("-").map(Number);
  return toIsoDate(new Date(Date.UTC(year, month - 1, day + days)));
};

/**
 * `addMonths`: the day is clamped to the month length (31 Jan + 1 month =
 * 28/29 Feb) and every offset is counted from the ORIGINAL date (31 Jan + 2
 * months = 31 Mar), never chained.
 */
export const addMonths = (isoDate: string, months: number): string => {
  const [year, month, day] = isoDate.split("-").map(Number);
  const totalMonths = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(totalMonths / 12);
  const targetMonth = totalMonths - targetYear * 12;
  const targetDay = Math.min(day, daysInMonth(targetYear, targetMonth));
  return `${targetYear}-${pad(targetMonth + 1)}-${pad(targetDay)}`;
};

const formatPct = (pct: number) => String(Number(pct.toFixed(6)));

/**
 * `calcMedida` validations of the custom ("a la medida") plan, with the same
 * Spanish messages as the SQL. Throws on the first problem.
 */
export const validateCustomPlan = (plan: {
  down_pct?: number | null;
  monthly_pct?: number | null;
  deed_pct?: number | null;
  months?: number | null;
  discount_pct?: number | null;
}): void => {
  const { down_pct, monthly_pct, deed_pct, months, discount_pct } = plan;
  if (
    down_pct == null ||
    monthly_pct == null ||
    deed_pct == null ||
    Number.isNaN(down_pct + monthly_pct + deed_pct) ||
    down_pct < 0 ||
    monthly_pct < 0 ||
    deed_pct < 0
  ) {
    throw new Error("Escribe los tres porcentajes");
  }
  const sum = down_pct + monthly_pct + deed_pct;
  if (Math.abs(sum - 100) > 0.01) {
    throw new Error(
      `Los tres porcentajes suman ${formatPct(Math.round(sum * 10) / 10)}%. Tienen que sumar 100%`,
    );
  }
  if (monthly_pct > 0 && (months == null || months < MIN_TERM_MONTHS)) {
    throw new Error("Si hay mensualidades, escribe cuántas");
  }
  if (months != null && months > MAX_TERM_MONTHS) {
    throw new Error("El plazo debe estar entre 1 y 36 meses");
  }
  if (discount_pct == null || discount_pct < 0 || discount_pct >= 100) {
    throw new Error("Escribe un descuento entre 0 y 99%");
  }
};

type BuildPlanInput = {
  gross: number;
  downPct: number;
  monthlyPct: number;
  deedPct: number;
  discountPct: number;
  /** Installments term in months (the project term or the custom plan's months). */
  plazo: number;
  issuedOn: string;
  validUntil: string;
  apartado: number;
};

/**
 * `planesDe` + `planCalendar` for an already-summed gross price. Final price =
 * round(gross * (1 - discount)); down payment = final * down% - apartado;
 * installments = final * monthly% / months (only when monthly% > 0); deed =
 * final * deed% at month term + 1. The apartado is dated at the issue date,
 * the down payment at the validity date, every other date is
 * `addMonths(validUntil, n)`. Amounts are not rounded to cents.
 */
export const buildPlan = (input: BuildPlanInput): QuoterPlanResult => {
  const { gross, downPct, monthlyPct, deedPct, discountPct, plazo } = input;
  const final = roundHalfUp((gross * (100 - discountPct)) / 100);
  const months = monthlyPct > 0 ? plazo : 0;
  const down = cleanAmount((final * downPct) / 100 - input.apartado);
  const installment =
    months > 0 ? cleanAmount((final * monthlyPct) / 100 / months) : 0;
  const deed = cleanAmount((final * deedPct) / 100);
  const deedMonth = plazo + 1;

  const calendar: QuoterCalendarRow[] = [
    {
      seq: 0,
      due_date: input.issuedOn,
      concept: "Apartado",
      amount: input.apartado,
    },
    {
      seq: 1,
      due_date: input.validUntil,
      concept: `Enganche a la firma · ${formatPct(downPct)}%`,
      amount: down,
    },
    ...Array.from({ length: months }, (_, index) => ({
      seq: 2 + index,
      due_date: addMonths(input.validUntil, index + 1),
      concept: `Mensualidad ${index + 1} de ${months}`,
      amount: installment,
    })),
    {
      seq: 2 + months,
      due_date: addMonths(input.validUntil, deedMonth),
      concept: `Contra escritura · ${formatPct(deedPct)}%`,
      amount: deed,
    },
  ];

  return {
    final,
    discount_amount: cleanAmount(gross - final),
    apartado: input.apartado,
    down_payment: down,
    installments: months,
    installment_amount: installment,
    deed_amount: deed,
    deed_month: deedMonth,
    plazo,
    issued_on: input.issuedOn,
    valid_until: input.validUntil,
    calendar,
  };
};
